import { APP_BASE_HREF, Location, AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { merge } from "rxjs";
import { filter, map } from "rxjs/operators";

import { LOCALE } from "@hermes/core-app-core";
import { isCanada, isUsa } from "@hermes/locale";

import { FocusDirective } from "@hermes/utils-generic-directives-focus";
import {
    isEndOfNavigation,
    urlWithoutFragment,
} from "@hermes/utils-generic-services-router";

import { removeQueryparamFromUrl } from "../../helpers/header.helper";

@Component({
    selector: "h-header-links",
    imports: [FocusDirective, AsyncPipe],
    templateUrl: "./header-links.component.html",
    styleUrls: ["./header-links.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderLinksComponent {
    public baseHref = inject(APP_BASE_HREF);
    private locale = inject(LOCALE);
    private location = inject(Location);
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    /**
     * Provide an observable to update skip links for both SSR and SPA routing :
     * - ActivatedRoute is used for initialisation (SSR)
     * - RouterEvent is then used when navigating inside the application (SPA routing)
     *
     * Trivia : ActivatedRoute is not updated after initialisation because current component is not inside a RouterOutlet.
     * RouterEvent fails to define initial value (unknown reason).
     */
    public skipLinks$ = merge(
        this.route.url.pipe(map(() => this.location.path())),
        this.router.events.pipe(
            filter(isEndOfNavigation),
            map(urlWithoutFragment),
        ),
    ).pipe(
        map((url: string) => ({
            content: this.anchorToSkipLink("#content", url),
            product: this.anchorToSkipLink("#collection-button", url),
            accessibility: this.getAccessibilityLink(),
        })),
    );

    public isCanada = isCanada(this.locale);
    public isUsa = isUsa(this.locale);
    public findAStoreUrl = `${this.locale.urlPrefix}/find-store/`;

    private anchorToSkipLink(anchor: string, url: string): string {
        // Removes size query parameter from URL
        const cleanUrl = removeQueryparamFromUrl(url, "size");
        return `${this.locale.urlPrefix}${cleanUrl}${anchor}`;
    }

    private getAccessibilityLink(): string {
        return `http://accessibility.hermes.com${this.locale.urlPrefix}/`;
    }
}
